import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { EmptyState } from '../../components/common/EmptyState';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { OrderDetailsModal } from '../../components/common/OrderDetailsModal';
import { PAGE_COLORS } from '../../utils/pageColors';
import { formatCurrencyGBP } from '../../utils/formatNumbers';
import { useGetJobsQuery } from '../../store/slices/apiSlice';
import type { JobSummary } from '../../utils/types';
import { formatJobDateCell } from '../../utils/jobDateDisplay';
import { Search, Filter, Plus, Eye, Edit2, ClipboardList, AlertTriangle, Shield } from 'lucide-react';

const PAGE_SIZE = 20;

export const JobsPage: React.FC = () => {
  // Filters and state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fulfillmentFilter, setFulfillmentFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [ordering, setOrdering] = useState('-created_at');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [viewOrdersModal, setViewOrdersModal] = useState<{
    jobId: string;
    customerName: string;
  } | null>(null);

  const params = useMemo(() => ({
    page: currentPage,
    page_size: PAGE_SIZE,
    search: searchTerm || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    fulfillment_type: fulfillmentFilter !== 'all' ? fulfillmentFilter : undefined,
    source_type: sourceFilter !== 'all' ? sourceFilter : undefined,
    ordering,
  }), [currentPage, searchTerm, statusFilter, fulfillmentFilter, sourceFilter, ordering]);

  const { data, isLoading, isError, error, refetch } = useGetJobsQuery(params);

  const jobs = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setFulfillmentFilter('all');
    setSourceFilter('all');
    setOrdering('-created_at');
    setCurrentPage(1);
  };
  const formatDate = (iso: string) => {
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
  };

  const renderPagination = () => {
    if (!totalCount || totalCount <= PAGE_SIZE) return null;
    return (
      <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
        <div className="text-sm text-gray-700">
          Showing {(currentPage - 1) * PAGE_SIZE + 1} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} jobs
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
            Previous
          </Button>
          <span className="px-3 text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
          <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <AppShell>
              <PageLayout
        title="Jobs Management"
        subtitle={`${totalCount} total jobs`}
          bgColor={PAGE_COLORS.jobs.bgColor}
          textColor={PAGE_COLORS.jobs.textColor}
          borderColor={PAGE_COLORS.jobs.borderColor}
        icon={<ClipboardList className="w-6 h-6" />}
        >
        <ContentCard
          title="All Jobs"
          subtitle="Search, filter and manage jobs"
          actions={
            <Link to="/jobs/new">
              <Button icon={<Plus className="w-4 h-4" />}>Create Job</Button>
            </Link>
          }
        >
          {/* Controls */}
          <div className="mb-6">
            <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 flex-1">
                <div className="relative flex-1 min-w-[240px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search name, email, job #, postcode..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>

                <div className="hidden sm:flex items-center gap-2 text-gray-700">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">Filters:</span>
                </div>

                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  className="form-input w-auto"
                >
                  <option value="all">All Status</option>
                  <option value="new">New</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="production">Production</option>
                </select>

                <select
                  value={fulfillmentFilter}
                  onChange={(e) => { setFulfillmentFilter(e.target.value); setCurrentPage(1); }}
                  className="form-input w-auto"
                >
                  <option value="all">All Fulfillment</option>
                  <option value="delivery">Delivery</option>
                  <option value="collection">Collection</option>
                  <option value="pallet">Pallet</option>
                </select>

                <select
                  value={sourceFilter}
                  onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(1); }}
                  className="form-input w-auto"
                >
                  <option value="all">All Sources</option>
                  <option value="manual">Manual</option>
                  <option value="website">Website</option>
                </select>

                <select
                  value={ordering}
                  onChange={(e) => { setOrdering(e.target.value); setCurrentPage(1); }}
                  className="form-input w-auto"
                >
                  <option value="-created_at">Newest</option>
                  <option value="created_at">Oldest</option>
                  <option value="-total_amount">Amount (High-Low)</option>
                  <option value="total_amount">Amount (Low-High)</option>
                  <option value="status">Status</option>
                  <option value="priority">Priority</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Button type="submit" variant="secondary">Search</Button>
                {(searchTerm || statusFilter !== 'all' || fulfillmentFilter !== 'all' || sourceFilter !== 'all' || ordering !== '-created_at') && (
                  <Button variant="secondary" size="sm" onClick={clearFilters}>Clear</Button>
                )}
              </div>
            </form>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {/* Error */}
          {isError && (
            <div className={`alert mb-6 ${('status' in (error as any) && (error as any).status === 403) ? 'alert-warning' : 'alert-error'}`}>
              <div className="flex items-center">
                {('status' in (error as any) && (error as any).status === 403) ? (
                  <Shield className="w-5 h-5 mr-2" />
                ) : (
                  <AlertTriangle className="w-5 h-5 mr-2" />
                )}
                <span className="mr-2">
                  {('status' in (error as any) && (error as any).status === 403)
                    ? "You don't have permission to view jobs. Ask admin for 'jobs_view' permission."
                    : 'Failed to load jobs. Please try again.'}
                </span>
                {!(('status' in (error as any) && (error as any).status === 403)) && (
                  <Button variant="ghost" size="sm" onClick={() => refetch()}>Retry</Button>
                )}
              </div>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !isError && jobs.length === 0 && (
          <EmptyState
              title="No jobs found"
              description={searchTerm ? 'Try adjusting search or filters.' : 'Create your first job to get started.'}
            action={
                <Link to="/jobs/new">
                  <Button icon={<Plus className="w-4 h-4" />}>Create Job</Button>
                </Link>
              }
            />
          )}

          {/* Table */}
          {!isLoading && !isError && jobs.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery Postcode</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Website Job #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="Glass Arrival Date">GAD</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="Date of Manufacture">DOM</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="Potential Delivery Date">PDD</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {jobs.map((job: JobSummary) => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {job.customer_first_name} {job.customer_last_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <Link
                            to={`/jobs/${job.id}/edit`}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                          >
                            {job.delivery_postcode || '—'}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {job.website_job_number || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            job.status === 'completed' ? 'bg-green-100 text-green-800' :
                            job.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            job.status === 'on_hold' ? 'bg-yellow-100 text-yellow-800' :
                            job.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {job.job_type === 'gc'
                            ? '—'
                            : formatJobDateCell(job.glass_arrival_date, false)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatJobDateCell(job.manufacture_date, false)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatJobDateCell(job.potential_delivery_date, true)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrencyGBP(job.total_amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(job.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setViewOrdersModal({
                                jobId: job.id,
                                customerName: `${job.customer_first_name || ''} ${job.customer_last_name || ''}`.trim() || 'Customer'
                              })}
                              icon={<Eye className="w-4 h-4" />}
                            >
                              View Orders
                            </Button>
                            <Link to={`/jobs/${job.id}`}>
                              <Button variant="ghost" size="sm" icon={<Eye className="w-4 h-4" />}>View</Button>
                            </Link>
                            <Link to={`/jobs/${job.id}/edit`}>
                              <Button variant="ghost" size="sm" icon={<Edit2 className="w-4 h-4" />}>Edit</Button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {renderPagination()}
            </>
          )}
        </ContentCard>
      </PageLayout>

      {/* View Orders Modal */}
      {viewOrdersModal && (
        <OrderDetailsModal
          isOpen={true}
          onClose={() => setViewOrdersModal(null)}
          jobId={viewOrdersModal.jobId}
          title={`Order Details - ${viewOrdersModal.customerName}`}
          mode="view"
        />
      )}
    </AppShell>
  );
};
